import type {Root} from 'hast'
import type {Point} from 'unist'
import {EXIT, visitParents} from 'unist-util-visit-parents'

import {isPositionedNode} from '../../syntax-tree/positioned-node'

/**
 * When hast-util-raw reparses the raw nodes in a document, it automatically closes any unclosed nodes. But it does
 * that autoclosing at the last non-whitespace character, trimming whitespace as it would be trimmed in a rendered
 * document. This doesn't work for contenteditable because it causes the elements to shrink and extend as the user
 * types new content in new blank lines. So this extension expands all the trailing elements in the doc to reach the
 * end of the document.
 */
export function hastExpandElementsToEnd(tree: Root, markdownSource: string): void {
  const lines = markdownSource.split('\n')
  const lastPosition = {
    // column and line are 1-based; offset is 0-based
    column: (lines.at(-1)?.length ?? 0) + 1,
    line: lines.length + 1,
    offset: markdownSource.length,
  } satisfies Point

  let lastDepth = -1
  visitParents(
    tree,
    (node, ancestors) => {
      // We only want the trailing nodes in the doc, so we exit as soon as we start moving horizontally or up in the tree
      if (ancestors.length <= lastDepth) return EXIT
      lastDepth = ancestors.length

      if (node.type !== 'element' || !isPositionedNode(node) || node.position.end.offset >= lastPosition.offset) return

      node.position.end = {...lastPosition}
    },
    true,
  )
}
